/**
 * GitHub Actions APK build status — Settings → App Updates.
 * Admin configures owner/repo (+ optional token for private repos);
 * the card then shows the latest CI builds of build-android-apk.yml
 * and a direct download link to the apk-latest release.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Github, CheckCircle2, XCircle, Loader2, Download, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const runBadge = (run) => {
  if (run.status === 'in_progress' || run.status === 'queued') {
    return <Badge className="bg-amber-100 text-amber-800 border-amber-300 gap-1"><Loader2 size={11} className="animate-spin" /> Building</Badge>;
  }
  if (run.conclusion === 'success') {
    return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 gap-1"><CheckCircle2 size={11} /> Success</Badge>;
  }
  return <Badge className="bg-rose-100 text-rose-800 border-rose-300 gap-1"><XCircle size={11} /> {run.conclusion || 'Failed'}</Badge>;
};

export default function ApkCiStatusCard() {
  const [repo, setRepo] = useState('');
  const [token, setToken] = useState('');
  const [hasToken, setHasToken] = useState(false);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/apk/ci-status');
      setStatus(data);
    } catch (e) {
      if (e?.response?.status !== 403) toast.error('Could not load CI status.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/apk/ci-config');
        setRepo(data.repo || '');
        setHasToken(data.has_token);
        if (data.configured) fetchStatus();
      } catch { /* non-admin */ }
    })();
  }, []);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/admin/apk/ci-config', { repo: repo.trim(), token: token.trim() });
      toast.success('GitHub CI settings saved.');
      setToken('');
      if (token.trim()) setHasToken(true);
      await fetchStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border-stone-300" data-testid="apk-ci-status-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-outfit flex items-center gap-2">
          <Github size={18} />
          APK Build Status (GitHub CI)
          <Badge variant="outline" className="text-[10px]">ADMIN</Badge>
        </CardTitle>
        <CardDescription>
          Live status of the <code>Build Android APK</code> workflow. Green means a fresh signed APK is ready on the <code>apk-latest</code> release.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={onSave} className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-end" data-testid="apk-ci-config-form">
          <div>
            <Label htmlFor="ci-repo" className="text-xs">Repository (owner/name)</Label>
            <Input id="ci-repo" placeholder="e.g. myuser/smartoerp" value={repo}
              onChange={(e) => setRepo(e.target.value)} data-testid="apk-ci-repo-input" />
          </div>
          <div>
            <Label htmlFor="ci-token" className="text-xs">
              GitHub token {hasToken ? <span className="text-emerald-600">(saved ✓)</span> : '(needed for private repos)'}
            </Label>
            <Input id="ci-token" type="password" placeholder={hasToken ? '••••••••  (leave blank to keep)' : 'ghp_…'}
              value={token} onChange={(e) => setToken(e.target.value)} data-testid="apk-ci-token-input" />
          </div>
          <Button type="submit" disabled={saving || !repo.trim()} size="sm" data-testid="apk-ci-save-btn">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </form>

        {status?.configured && (
          <div className="rounded-lg border bg-stone-50 p-3" data-testid="apk-ci-runs">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-stone-800">Recent builds — {status.repo}</span>
              <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={fetchStatus} disabled={loading} data-testid="apk-ci-refresh-btn">
                <RefreshCw size={12} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
            {status.error ? (
              <p className="text-xs text-rose-700" data-testid="apk-ci-error">{status.error}</p>
            ) : status.runs.length === 0 ? (
              <p className="text-xs text-stone-500">No builds yet. Push to main (or run the workflow manually) to start one.</p>
            ) : (
              <div className="space-y-1.5">
                {status.runs.map((run) => (
                  <div key={run.run_number} className="flex items-center gap-2 text-xs flex-wrap" data-testid={`apk-ci-run-${run.run_number}`}>
                    {runBadge(run)}
                    <span className="font-mono text-stone-600">#{run.run_number}</span>
                    <span className="text-stone-500 truncate flex-1 min-w-0">{run.commit_message}</span>
                    <span className="text-stone-400 font-mono">{(run.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                    <a href={run.html_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline inline-flex items-center gap-0.5">
                      logs <ExternalLink size={10} />
                    </a>
                  </div>
                ))}
              </div>
            )}
            {status.apk_release_url && (
              <a href={status.apk_release_url} target="_blank" rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:underline"
                data-testid="apk-ci-download-link">
                <Download size={12} /> Download latest built APK (apk-latest release)
              </a>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
